import { createHttpTransport, TransportAdapter } from '@indicia/adk-vue-core'
import axios from 'axios'

/** Seconds before expiry at which a token is renewed, so no request leaves with one that just expired. */
const TOKEN_RENEW_MARGIN_SECONDS = 60

interface IAssistantToken {
    value: string
    expiresAt: number
}

/**
 * Talks to the Momento Assistent: the bearer token the Momento backend issues per group,
 * the ADK transport the chat runs over, and the health check the navigation uses.
 */
class AssistantService {
    private tokens = new Map<number, IAssistantToken>()

    /** The assistant's API root: the dev proxy locally, the configured URL in production. */
    get apiBase(): string {
        const base = import.meta.env.PROD ? window.__env.ragApiBase : import.meta.env.VITE_VUE_APP_RAG_API_BASE
        if (!base) throw new Error('De URL van de Momento Assistent is niet ingesteld')
        return base.replace(/\/$/, '')
    }

    /** A valid token for the group, fetched from the Momento backend when none is held or it is about to expire. */
    async getToken(groupId: number): Promise<string> {
        const held = this.tokens.get(groupId)
        if (held && held.expiresAt - TOKEN_RENEW_MARGIN_SECONDS > Date.now() / 1000) return held.value

        const response = await axios.get<{ token: string }>('auth/assistent-token', { params: { stamgroepId: groupId } })
        const value = response.data.token
        this.tokens.set(groupId, { value, expiresAt: this.readExpiry(value) })
        return value
    }

    /** The ADK transport for one group: every request carries a fresh token for that group. */
    createTransport(groupId: number): TransportAdapter {
        return createHttpTransport({
            baseUrl: `${this.apiBase}/api/v1`,
            credentials: 'omit',
            headers: async () => ({ Authorization: `Bearer ${await this.getToken(groupId)}` }),
        })
    }

    /** Whether the assistant is reachable; the navigation only offers it when it is. */
    async isHealthy(): Promise<boolean> {
        try {
            const response = await fetch(`${this.apiBase}/health`)
            return response.ok
        } catch {
            return false
        }
    }

    private readExpiry(token: string): number {
        try {
            const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
            return typeof payload.exp === 'number' ? payload.exp : 0
        } catch {
            return 0
        }
    }
}

export const assistantService = new AssistantService()
